import { useCallback, useEffect, useState } from "react";
import { SortDirection } from "../../../models/Enum";
import api from "../../../services/ApiService";
import { AttachmentBasicDetail } from "../../../models/Attachments/AttachmentBasicDetail";
import { ApiResponse } from "../../../models/ApiResponse";
import { DataGrid, GridColDef, GridPaginationModel, GridSortModel, GridToolbarColumnsButton, GridToolbarContainer, GridToolbarQuickFilter } from "@mui/x-data-grid";
import moment from "moment";
import { DATE_TIME_FORMAT } from "../../../utils/constants";
import { useAppDispatch, useAppSelector } from "../../../store";
import { updateAttachmentListRequest } from "../../../reducers/attachmentsReducer";
import { AttachmentSelectedFilters } from "../../../models/Attachments/AttachmentListRequest";
import imageNotAvailable from "../../../no-image.png";
import DownloadIcon from '@mui/icons-material/Download';
import { Box, Button, CardMedia, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Tooltip, Typography } from "@mui/material";
import ImageViewModal, { ImageDetail } from "./ImageViewModal";
import GetFileType from "../../../components/GetFileType";
import GetIconByFileType from "../../../components/GetIconByFileType";
import PDFViewModal, { PDFDetail } from "./PDFViewModal";
import VisibilityIcon from '@mui/icons-material/Visibility';
import { OpenInBrowser } from "@mui/icons-material";
import ImageSliderZoom from "../../../components/ImageSliderZoom";
import { THUMBNAIL_IMAGE_API_URL, VIEW_ATTACHMENTS } from "../../Products/EditItem/Constants";
import Filters from "../../Products/Filters";
import { toast } from "react-toastify";

export interface AttachmentListResponse {
    attachments?: AttachmentBasicDetail[]
    totalCount?: number
};

type SliderImageDetail = {
    fileName: string; // link / full path used by API
    imageFileName: string; // display name
    imageUrl: string; // thumbnail url
    attachmentType?: string;
    attachmentDate?: string;
};

export const initialAttachmentListRequest = {
    pageIndex: 1,
    pageSize: 10,
    sortBy: "baseCode",
    sortDirection: SortDirection.DESC,
    hideReleasedToInriver: true,
    hideArchivedDevelopment: true,
};

const AttachmentList = () => {

    const dispatch = useAppDispatch();
    const attachmentListRequestState = useAppSelector((state) => state.attachments.attachmentListRequest);

    const [attachments, setAttachments] = useState<AttachmentBasicDetail[]>([]);
    const [attachmentsTotalCount, setAttachmentsTotalCount] = useState(0);
    const [searchFlag, setSearchFlag] = useState(false);
    const [imageDetailView, setImageDetailView] = useState<ImageDetail>();
    const [isImageViewOpen, setIsImageViewOpen] = useState(false);
    const [isPDFViewOpen, setIsPDFViewOpen] = useState(false);
    const [pdfDetailView, setPDFDetailView] = useState<PDFDetail>();
    const [filtersExpanded, setFiltersExpanded] = useState<boolean>(true);

    const [isFolderDialogOpen, setIsFolderDialogOpen] = useState(false);
    const [folderLoading, setFolderLoading] = useState(false);
    const [selectedFolderPath, setSelectedFolderPath] = useState<string>("");
    const [selectedFolderName, setSelectedFolderName] = useState<string>("");
    const [folderContents, setFolderContents] = useState<{ fileName: string; link: string }[]>([]);

    const [folderSliderImages, setFolderSliderImages] = useState<SliderImageDetail[]>([]);
    const [isFolderSliderOpen, setIsFolderSliderOpen] = useState(false);
    const [selectedFolderSlide, setSelectedFolderSlide] = useState(0);

    const closeView = () => {
        setIsImageViewOpen(false);
        setIsPDFViewOpen(false);
    };

    const closeFolderDialog = () => {
        setIsFolderDialogOpen(false);
        setFolderLoading(false);
        setSelectedFolderPath("");
        setSelectedFolderName("");
        setFolderContents([]);
    };

    const closeFolderSlider = () => {
        setIsFolderSliderOpen(false);
        setFolderSliderImages([]);
        setSelectedFolderSlide(0);
    };


    const AttachmentListGridColumns: GridColDef[]= [
        {
            field:"prjCode", 
            headerName: "Project Code",
            width: 140,
        },
        {
            field:"stockCode", 
            headerName: "Stock Code",
            width: 170,
        },
        {
            field:"baseCode", 
            headerName: "Base Code",
            width: 150,
        },
        {
            field:"attachmentTypeDisplayText", 
            headerName:"Attachment Type",
            width: 200,
        },
        {
            field: "attachmentDate",
            headerName: "Attachment Date",
            width: 220,
            valueFormatter: (params) => moment.utc(params?.value).local().format(DATE_TIME_FORMAT),
        },
        {
            field: "fileName",
            headerName: "File Name",
            width: 200
        },
        {
            field: "level",
            headerName: "Level",
            width: 200
        },
        {
            field: "image",
            headerName: "Preview",
            renderCell: (params) => {
                return(
                    <div className="attachment-grid-image-cell">
                        <Tooltip title={params.row.link || ""} arrow>
                        {   //if the file type is image or not
                            GetFileType(params.row.fileName).isImage 
                            ?
                            <Box className = "image-thumbnail-container">
                                <CardMedia
                                    component = "img"
                                    src={params.value} 
                                    alt={`Attachment ${params.row.link}`} 
                                    onError={({ currentTarget }) => {
                                        currentTarget.onerror = null;
                                        currentTarget.src = imageNotAvailable;
                                    }}
                                />
                                <div style={{ position: 'absolute', display: 'flex', gap: '5px' }}>
                                    <IconButton
                                        className="attachment-hover-icon-button"
                                        size="small"
                                        onClick={() => handleAttachmentDownload(params.row.link)}
                                    >
                                        <DownloadIcon fontSize="small" />
                                    </IconButton>
                                    <IconButton
                                        className="attachment-hover-icon-button"
                                        size="small"
                                        onClick={() => handleImageSlider(params.row.link)}
                                    >
                                        <VisibilityIcon fontSize="small" />
                                    </IconButton>
                                </div>
                            </Box> 
                            : 
                            //else display according icon
                            <Box className = "image-thumbnail-container"
                                    sx = {{
                                        backgroundColor: "rgba(0,0,0,.05)",
                                        height: "66px",
                                        width: "66px",
                                        textAlign: "center",
                                    }}
                            >
                                {   //getting the Icon 
                                    GetIconByFileType(params.row.fileName)}
                                <div style={{ position: 'absolute', display: 'flex', gap: '5px' }}>
                                    {!(GetFileType(params.row.fileName).isFolder) && <IconButton
                                        className="attachment-hover-icon-button"
                                        size="small"
                                        onClick={() => handleAttachmentDownload(params.row.link)}
                                    >
                                        <DownloadIcon fontSize="small" />
                                    </IconButton>}
                                    {GetFileType(params.row.fileName).isFolder && (
                                        <>
                                            <IconButton
                                                className="attachment-hover-icon-button"
                                                size="small"
                                                onClick={() => downloadFolderZip(params.row.link)}
                                            >
                                                <DownloadIcon fontSize="small" />
                                            </IconButton>
                                            <IconButton
                                                className="attachment-hover-icon-button"
                                                size="small"
                                                onClick={() => handleOpenFolder(params.row.fileName, params.row.link)}
                                            >
                                                <OpenInBrowser fontSize="small" />
                                            </IconButton>
                                        </>
                                    )}
                                    {GetFileType(params.row.fileName).isPdf  && (<IconButton
                                        className="attachment-hover-icon-button"
                                        size="small"
                                        onClick={() => handlePDFViewer(params.row.link)}
                                    >
                                        <VisibilityIcon fontSize="small" />
                                    </IconButton>)}
                                </div>
                            </Box> 
                        }
                        </Tooltip>
                    </div>
                )
            },
            width: 100,
            cellClassName: "sidebarImageListing",
            sortable:false
        },
    ];

    const fetchAttachments = useCallback(async () => {
        try{
            setAttachments([]);
            setAttachmentsTotalCount(0);
            api 
                .post<ApiResponse<AttachmentListResponse>>("/Attachment/list-filter", attachmentListRequestState)
                .then(async (response) => {
                    if(response.isSuccess){
                        setAttachmentsTotalCount(response?.data?.totalCount);
                        const attachments: AttachmentBasicDetail[] = (response?.data?.attachments || []);
                        const mappedAttachments = attachments.map((attachment, index) => ({
                            ...attachment,
                            //setting the ID to index since there are no unique ID attachId && PrjCode may be duplicate
                            id: index + 1,
                            image: attachment.link ? `${THUMBNAIL_IMAGE_API_URL}?fileName=${encodeURIComponent(attachment.link)}` : imageNotAvailable
                        }));
                        setAttachments(mappedAttachments);
                    }
                })
                .catch((err: any) => {});
        }catch (error){
            console.error("Exception from attachment list", error);
        }
    }, [attachmentListRequestState]);

    useEffect(() => {
        if(searchFlag){
            fetchAttachments();
        }
    },[fetchAttachments, searchFlag]);

    const handleApplyFilterClick = (filterValues: AttachmentSelectedFilters) => {
        setSearchFlag(true);
        if (filterValues.projectCode) {
            dispatch(updateAttachmentListRequest({
              ...attachmentListRequestState,
              projectCode: filterValues.projectCode,
              pageIndex: 1,
            }));
        } else if (Object.values(filterValues)?.length) {
            dispatch(updateAttachmentListRequest({
              pageSize:attachmentListRequestState.pageSize, 
              sortBy: attachmentListRequestState.sortBy, 
              sortDirection: attachmentListRequestState.sortDirection,
              ...filterValues,
              pageIndex: 1,
            }));
        } else {
            //reset the filters
            setSearchFlag(false);
            dispatch(updateAttachmentListRequest(initialAttachmentListRequest));
        }
    };

    const getSortFieldName = (gridFieldName: string): string => {
        return gridFieldName;
    };

    const onSortChange = (sorting: GridSortModel) => {
        if (sorting?.length === 0) {
            dispatch(updateAttachmentListRequest({
                ...attachmentListRequestState,
                sortBy: attachmentListRequestState.sortBy || initialAttachmentListRequest.sortBy,
                sortDirection: attachmentListRequestState?.sortDirection === SortDirection.DESC ? SortDirection.ASC : SortDirection.DESC
            }));
        } else {
            dispatch(updateAttachmentListRequest({
                ...attachmentListRequestState,
                sortBy: getSortFieldName(sorting[0].field),
                sortDirection:
                    sorting[0].sort === "asc" ? SortDirection.ASC : SortDirection.DESC,
            }));
        }
    };

    const onPaginationChange = (pagination: GridPaginationModel) => {
        dispatch(updateAttachmentListRequest({
            ...attachmentListRequestState,
            pageIndex: pagination.page + 1,
            pageSize: pagination.pageSize,
        }));
    };

    const gridToolBar = () => (
        <GridToolbarContainer>
            <div style={{ flexGrow: 1, textAlign: "center", padding: 5,
                display: "flex", justifyContent: "flex-start"  }}>
                <GridToolbarColumnsButton />                  
                <GridToolbarQuickFilter sx ={{width: "20%", marginLeft: "1%"}}/>
            </div>
        </GridToolbarContainer>
    );

    const handleImageSlider = (link) => {
        setImageDetailView({
            fileName: link,
            imageUrl: `${THUMBNAIL_IMAGE_API_URL}?fileName=${encodeURIComponent(link)}&height=700&width=700`
        });
        setIsImageViewOpen(true);
    };

    const handleAttachmentDownload = async (link: string) => {
        try{
            window.open(`${process.env.REACT_APP_API_URL}/Attachment/RetrieveFile?fileName=${encodeURIComponent(link)}`, '_blank');
        } catch (error: any) {
            console.error("Exception from RetrieveFile API", error);
        }
    };

    const downloadFolderZip = async (folderPath: string) => {
        if (!folderPath) {
            toast.error("Folder path is missing");
            return;
        }

        try {
            const response = await api.get<ApiResponse<string>>(
                `/Attachment/extract-folder-zip?folderPath=${encodeURIComponent(folderPath)}`
            );

            if (response?.isSuccess && response?.data) {
                window.open(
                    `${process.env.REACT_APP_API_URL}/Attachment/download-attachment-zip?fileName=${encodeURIComponent(response.data)}`,
                    "_blank"
                );
            } else {
                toast.error(response?.message || "Error while downloading folder");
            }
        } catch (error) {
            console.error("Error while downloading folder:", error);
            toast.error("Error while downloading folder");
        }
    };
    
    const handlePDFViewer = (link) => {
        console.log(link);
        setPDFDetailView({
            fileName: link,
        });
        setIsPDFViewOpen(true);
    }

    const openImagePreviewFromFolder = (imageLink: string) => {
        const imageItems = folderContents.filter((f) => GetFileType(f.fileName).isImage);
        const images: SliderImageDetail[] = imageItems.map((item) => ({
            fileName: item.link,
            imageFileName: item.fileName,
            imageUrl: `${THUMBNAIL_IMAGE_API_URL}?fileName=${encodeURIComponent(item.link)}&height=700&width=700`,
        }));

        if (!images.length) return;

        setFolderSliderImages(images);
        setIsFolderSliderOpen(true);
        setSelectedFolderSlide(Math.max(0, imageItems.findIndex((x) => x.link === imageLink)));
    };

    const openPdfPreviewFromFolder = (link: string) => {
        handlePDFViewer(link);
    };

    const fetchFolderContents = async (folderPath: string) => {
        setFolderLoading(true);
        setFolderContents([]);

        try {
            const response = await api.get<ApiResponse<string[]>>(
                `/Attachment/retrive-folder-contents?folderPath=${encodeURIComponent(folderPath)}`
            );

            if (response?.isSuccess && response?.data?.length) {
                setFolderContents(
                    response.data.map((fileName) => ({
                        fileName,
                        link: `${folderPath}/${fileName}`,
                    }))
                );
            } else {
                setFolderContents([]);
            }
        } catch (error) {
            console.error("Error fetching folder contents!", error);
            setFolderContents([]);
        } finally {
            setFolderLoading(false);
        }
    };

    const handleOpenFolder = async (folderName: string, folderPath: string) => {
        setSelectedFolderName(folderName || "Folder");
        setSelectedFolderPath(folderPath || "");
        setIsFolderDialogOpen(true);

        if (folderPath) {
            await fetchFolderContents(folderPath);
        }
    };

    const handleExpandClick = () => {
        setFiltersExpanded(!filtersExpanded);
      };

    return (
        <>
            {/* <AttachmentFilter 
                onApplyFilter = {handleApplyFilterClick}
            /> */}
            <Filters
                onApplyFilter={handleApplyFilterClick}
                filtersExpanded={filtersExpanded}
                handleExpandClick={handleExpandClick}
                viewMode={VIEW_ATTACHMENTS}
            />
            <ImageViewModal
                images = {imageDetailView}
                isOpen = {isImageViewOpen}
                onClose = {closeView}
            />
            {(folderSliderImages.length > 0) && (
                <Box className="attachment-list image-slider-container">
                    <ImageSliderZoom
                        images={folderSliderImages}
                        selectedImageSlide={selectedFolderSlide}
                        isOpen={isFolderSliderOpen}
                        onClose={closeFolderSlider}
                    />
                </Box>
            )}
            { 
            isPDFViewOpen && <PDFViewModal
                pdf={pdfDetailView}
                isOpen = {isPDFViewOpen}
                onClose = {closeView}
            />
            }

            <Dialog
                open={isFolderDialogOpen}
                onClose={closeFolderDialog}
                fullWidth
                maxWidth="lg"
            >
                <DialogTitle>
                    <Box display="flex" alignItems="center" justifyContent="space-between" gap={2}>
                        <Typography
                            variant="h6"
                            sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                            title={`${selectedFolderName || "Folder"} contents`}
                        >
                            {selectedFolderName || "Folder"} contents
                        </Typography>
                        <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                            title={selectedFolderPath || ""}
                        >
                            {selectedFolderPath || ""}
                        </Typography>
                    </Box>
                </DialogTitle>
                <DialogContent dividers>
                    {folderLoading ? (
                        <Box display="flex" justifyContent="center" py={4}>
                            <CircularProgress />
                        </Box>
                    ) : folderContents.length ? (
                        <Box
                            sx={{
                                display: "flex",
                                flexWrap: "wrap",
                                gap: 2,
                                alignItems: "flex-start",
                                justifyContent: "flex-start",
                            }}
                        >
                            {folderContents.map((item) => {
                                const type = GetFileType(item.fileName);
                                const isImage = type.isImage;
                                const thumbUrl = isImage
                                    ? `${THUMBNAIL_IMAGE_API_URL}?fileName=${encodeURIComponent(item.link)}&height=220&width=220`
                                    : "";

                                return (
                                    <Box
                                        key={item.link}
                                        sx={{
                                            flex: "1 1 240px",
                                            maxWidth: 280,
                                            border: "1px solid rgba(0,0,0,0.12)",
                                            borderRadius: 1,
                                            overflow: "hidden",
                                            backgroundColor: "#fff",
                                        }}
                                    >
                                        <Box
                                            className="image-thumbnail-container"
                                            sx={{
                                                position: "relative",
                                                height: 150,
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                backgroundColor: "rgba(0,0,0,.05)",
                                            }}
                                        >
                                            {isImage ? (
                                                <CardMedia
                                                    component="img"
                                                    src={thumbUrl}
                                                    alt={item.fileName}
                                                    onError={({ currentTarget }) => {
                                                        currentTarget.onerror = null;
                                                        currentTarget.src = imageNotAvailable;
                                                    }}
                                                    sx={{
                                                        height: "100%",
                                                        width: "100%",
                                                        objectFit: "contain",
                                                    }}
                                                />
                                            ) : (
                                                <Box sx={{ transform: "scale(1.1)" }}>
                                                    {GetIconByFileType(item.fileName)}
                                                </Box>
                                            )}

                                            <Box sx={{ position: "absolute", display: "flex", gap: "6px" }}>
                                                {!type.isFolder && (
                                                    <IconButton
                                                        className="attachment-hover-icon-button"
                                                        size="small"
                                                        onClick={() => handleAttachmentDownload(item.link)}
                                                    >
                                                        <DownloadIcon fontSize="small" />
                                                    </IconButton>
                                                )}
                                                {type.isFolder && (
                                                    <>
                                                        <IconButton
                                                            className="attachment-hover-icon-button"
                                                            size="small"
                                                            onClick={() => downloadFolderZip(item.link)}
                                                        >
                                                            <DownloadIcon fontSize="small" />
                                                        </IconButton>
                                                        <IconButton
                                                            className="attachment-hover-icon-button"
                                                            size="small"
                                                            onClick={() => handleOpenFolder(item.fileName, item.link)}
                                                        >
                                                            <OpenInBrowser fontSize="small" />
                                                        </IconButton>
                                                    </>
                                                )}
                                                {type.isImage && (
                                                    <IconButton
                                                        className="attachment-hover-icon-button"
                                                        size="small"
                                                        onClick={() => openImagePreviewFromFolder(item.link)}
                                                    >
                                                        <VisibilityIcon fontSize="small" />
                                                    </IconButton>
                                                )}
                                                {type.isPdf && (
                                                    <IconButton
                                                        className="attachment-hover-icon-button"
                                                        size="small"
                                                        onClick={() => openPdfPreviewFromFolder(item.link)}
                                                    >
                                                        <VisibilityIcon fontSize="small" />
                                                    </IconButton>
                                                )}
                                            </Box>
                                        </Box>

                                        <Tooltip title={item.link} arrow>
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    px: 1,
                                                    py: 0.75,
                                                    overflow: "hidden",
                                                    textOverflow: "ellipsis",
                                                    whiteSpace: "nowrap",
                                                }}
                                            >
                                                {item.fileName}
                                            </Typography>
                                        </Tooltip>
                                    </Box>
                                );
                            })}
                        </Box>
                    ) : (
                        <Typography variant="body2" color="text.secondary">
                            No folder items found.
                        </Typography>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeFolderDialog}>Close</Button>
                </DialogActions>
            </Dialog>

            {searchFlag && <DataGrid 
                rows={attachments}
                sortingOrder={['desc', 'asc']}
                columns={AttachmentListGridColumns} 
                initialState={{
                    pagination: {
                      paginationModel: {
                        pageSize: 10
                      },
                    },
                }}
                disableColumnMenu={true}
                sortModel={[
                    {
                      field: attachmentListRequestState.sortBy,
                      sort:
                      attachmentListRequestState.sortDirection === SortDirection.ASC
                          ? "asc"
                          : "desc",
                    },
                  ]}
                rowCount={attachmentsTotalCount}
                pageSizeOptions={[10, 25, 50, 100]}
                sortingMode="server"
                paginationMode="server"
                onSortModelChange={onSortChange}
                onPaginationModelChange={onPaginationChange}
                slots={{
                    toolbar: gridToolBar,
                }}
                paginationModel={{
                    page: attachmentListRequestState.pageIndex - 1, 
                    pageSize: attachmentListRequestState.pageSize,
                }}
                getRowHeight={() => 'auto'}
                rowSelection={false}
            />}
        </>
    )

};

export default AttachmentList;